import { AppPageLayout } from "@/components/common/AppPageLayout";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useScreenerCopy } from "@/features/i18n/screenerCopy";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { FormPublicCover } from "@/features/pages/components/FormPublicCover";
import { formPageColumnClass, publicFormRoutePaddingClass } from "@/features/pages/components/formPageLayout";
import { useFormNavigationStore } from "@/features/pages/hooks/useFormNavigationStore";
import {
    formDisplayOptions,
    formInstructionsPlacement,
    formTitleAlign,
    trimmedField,
} from "@/features/pages/utils/formChrome";
import { useSelectScreener } from "@/features/screeners/api/selectScreener";
import ScreenerUnavailable from "@/features/screeners/components/ScreenerUnavailable";
import { parseScreenerSearch } from "@/features/screeners/utils/screenerSearch";
import { Skeleton } from "@patient-watch/ui/skeleton";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

const ScreenerLandingPage = () => {
    const { screenerId } = Route.useParams();
    const search = Route.useSearch();
    const navigate = useNavigate();
    const { locale } = useAppLocale();
    const copy = useScreenerCopy();
    const resetEntryValues = useEntryValuesStore((state) => state.resetEntryValues);
    const [isStarting, setIsStarting] = useState(false);
    const { data: screener, isLoading: isLoadingScreener } = useSelectScreener(screenerId);
    const { data: form, isLoading: isLoadingForm } = useSelectFormById(screener?.form_id ?? "", locale);

    const handleStart = () => {
        if (isStarting) return;
        setIsStarting(true);
        resetEntryValues();
        // Each attempt gets its own id, so answers from an earlier go are never carried over.
        const entryId = crypto.randomUUID();
        useFormNavigationStore.getState().resetHistory(entryId);
        void navigate({
            to: "/screen/$screenerId/$entryId/page/$pageIndex",
            params: { screenerId, entryId, pageIndex: "1" },
            search,
        });
    };

    if (isLoadingScreener || isLoadingForm) {
        return (
            <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
                <div className={`${formPageColumnClass} gap-4`}>
                    <Skeleton className="w-3/4 h-8" />
                    <Skeleton className="w-full h-4" />
                    <Skeleton className="w-2/3 h-4" />
                </div>
            </AppPageLayout>
        );
    }

    if (!screener || !form) return <ScreenerUnavailable />;

    const numberOfQuestions = (form.pages ?? []).reduce((count, page) => count + (page.inputs?.length ?? 0), 0);

    return (
        <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
            <FormPublicCover
                name={trimmedField(form.form_name)}
                caption={trimmedField(form.form_caption)}
                kicker={trimmedField(form.form_kicker)}
                copyright={trimmedField(form.copyright_info)}
                instructions={formInstructionsPlacement(form) === "cover" ? trimmedField(form.form_instructions) : null}
                timeToComplete={form.time_to_complete || null}
                numberOfQuestions={numberOfQuestions > 0 ? numberOfQuestions : null}
                titleAlign={formTitleAlign(form)}
                showMetrics={formDisplayOptions(form).showCoverMetrics}
                startLabel={isStarting ? copy.starting : copy.start}
                isStarting={isStarting}
                onStart={handleStart}
            />
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/screen/$screenerId/")({
    component: ScreenerLandingPage,
    validateSearch: parseScreenerSearch,
});
